/**
 * m3 echo Frontend - Autopilot Quota Card
 *
 * Admin view of a tenant's monthly autopilot quota: the two-bar panel that
 * every other quota view shares, the ledger of bookings behind this month's
 * numbers, and a support escape hatch to correct one when a booking is
 * wrong. Corrections never rewrite a counter - `adjustTenantQuota` appends a
 * new `admin_adjustment` ledger row (`booking_service.admin_adjust`), so the
 * correction stays visible next to the booking it offsets.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Button,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getTenantAutopilotQuota, QuotaBooking, ScheduleType } from '../../services/autopilots';
import { adjustTenantQuota } from '../../services/admin';
import { describeApiError } from '../../services/api';
import MonthlyQuotaPanel from '../quota/MonthlyQuotaPanel';
import { invalidateQuotaViews } from '../quota/quotaQueries';

/**
 * Ledger wording, kept identical to `AutopilotManagementCard` so the same
 * booking reads the same way in the customer-facing and the admin view.
 */
const CADENCE_LABELS: Record<ScheduleType, string> = {
  daily: 'Täglich',
  weekly: 'Wöchentlich',
  monthly: 'Monatlich',
};

const BOOKING_KIND_LABELS: Record<QuotaBooking['kind'], string> = {
  initial: 'Erstbuchung',
  renewal: 'Monatsverlängerung',
  increase: 'Erhöhung',
  admin_adjustment: 'Manuelle Anpassung',
};

interface Props {
  tenantId: number;
  /**
   * Told after a correction is booked. The tenant table behind this card is
   * state-backed, not query-backed, so no invalidation can reach it - it kept
   * showing the pre-correction figure until the page was reloaded.
   */
  onQuotaChanged?: () => void;
}

interface AdjustDialogProps {
  open: boolean;
  onClose: () => void;
  tenantId: number;
  bookings: QuotaBooking[];
  onAdjusted?: () => void;
}

/** Every autopilot the current month's ledger names, deduplicated - the only
 * autopilots a correction can sensibly be attributed to. A deleted
 * autopilot's booking still lists it by amount but no autopilot_id survives
 * to offer here; correcting that one is what leaving the selector on "kein
 * Autopilot" is for. */
const autopilotOptionsFrom = (bookings: QuotaBooking[]) => {
  const seen = new Map<number, string>();
  bookings.forEach((booking) => {
    if (booking.autopilot_id !== null && !seen.has(booking.autopilot_id)) {
      seen.set(booking.autopilot_id, booking.project_name ?? `Autopilot #${booking.autopilot_id}`);
    }
  });
  return Array.from(seen.entries()).map(([id, label]) => ({ id, label }));
};

const AdjustQuotaDialog: React.FC<AdjustDialogProps> = ({
  open,
  onClose,
  tenantId,
  bookings,
  onAdjusted,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();

  const [amountInput, setAmountInput] = useState('');
  const [autopilotId, setAutopilotId] = useState<number | ''>('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const autopilotOptions = useMemo(() => autopilotOptionsFrom(bookings), [bookings]);

  // A fresh form every time the dialog opens, not whatever the previous
  // support case left behind.
  useEffect(() => {
    if (!open) return;
    setAmountInput('');
    setAutopilotId('');
    setReason('');
    setError(null);
  }, [open]);

  const adjustMutation = useMutation({
    mutationFn: (input: { amount: number; autopilot_id?: number | null; reason: string }) =>
      adjustTenantQuota(tenantId, input),
    onSuccess: () => {
      // A correction moves the same numbers a booking moves, so it invalidates
      // the same views - including the booking preview, which would otherwise
      // keep answering "passt" against a budget that just changed.
      invalidateQuotaViews(queryClient);
      onAdjusted?.();
      onClose();
    },
    onError: (err: unknown) => {
      setError(describeApiError(err, 'Die Korrektur konnte nicht gespeichert werden.'));
    },
  });

  // `Number`, not `parseInt`: "12x" would silently book 12 while the field
  // still shows "12x" - a value and a field that disagree.
  const parsedAmount = amountInput.trim() === '' ? Number.NaN : Number(amountInput);
  const amountValid = Number.isInteger(parsedAmount) && parsedAmount !== 0;
  const reasonValid = reason.trim().length >= 3;
  const canSubmit = amountValid && reasonValid && !adjustMutation.isPending;

  const handleSubmit = () => {
    if (!canSubmit) return;
    setError(null);
    adjustMutation.mutate({
      amount: parsedAmount,
      autopilot_id: autopilotId === '' ? null : autopilotId,
      reason: reason.trim(),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>Kontingent korrigieren</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Schreibt eine neue Buchungszeile mit der Art „Manuelle Anpassung“ - bestehende Buchungen
          werden nie verändert. Ein negativer Betrag gibt Kontingent frei, ein positiver bucht
          zusätzliches.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={2}>
          <TextField
            label="Betrag"
            type="number"
            value={amountInput}
            onChange={(event) => setAmountInput(event.target.value)}
            error={amountInput.trim() !== '' && !amountValid}
            helperText="Negativ, um gebuchtes Kontingent freizugeben; positiv, um zusätzliches zu buchen."
            fullWidth
            autoFocus
          />
          <TextField
            select
            label="Autopilot (optional)"
            value={autopilotId}
            onChange={(event) =>
              setAutopilotId(event.target.value === '' ? '' : Number(event.target.value))
            }
            fullWidth
            helperText="Ohne Auswahl wird die Korrektur keinem Autopilot zugeordnet - auch der richtige Weg, um die Buchung eines bereits gelöschten Autopilots zu korrigieren."
          >
            <MenuItem value="">Kein Autopilot</MenuItem>
            {autopilotOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label} (#{option.id})
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Begründung"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            error={reason.length > 0 && !reasonValid}
            helperText="Wird im Audit-Log gespeichert. Mindestens 3 Zeichen."
            multiline
            minRows={2}
            fullWidth
            required
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={adjustMutation.isPending}>
          Abbrechen
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit}>
          {adjustMutation.isPending ? <CircularProgress size={24} /> : 'Korrektur speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const AutopilotQuotaCard: React.FC<Props> = ({ tenantId, onQuotaChanged }) => {
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);

  const {
    data: quotaInfo,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['tenantAutopilotQuota', tenantId],
    queryFn: () => getTenantAutopilotQuota(tenantId),
    enabled: !!tenantId,
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  /**
   * A failed read says so. It used to fall through to `return null` and the
   * card simply was not there - indistinguishable, to the administrator
   * looking at the tenant, from a tenant that has no quota view at all, with
   * nothing to retry and nothing to report.
   */
  if (isError) {
    return (
      <Card>
        <CardHeader title="Monatskontingent Autopilots" />
        <CardContent>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => refetch()} disabled={isFetching}>
                Erneut laden
              </Button>
            }
          >
            Das Monatskontingent dieses Mandanten konnte nicht geladen werden.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Reached only while the query is disabled, i.e. without a tenant to ask
  // about. Nothing to show and nothing to report.
  if (!quotaInfo) {
    return null;
  }

  return (
    <Card>
      <CardHeader
        title="Monatskontingent Autopilots"
        subheader={`${quotaInfo.active_autopilots_count} aktive Autopilots`}
      />
      <CardContent>
        {/*
          Full card width, not a narrow column: both bars share one scale, so
          the bar that fits renders shorter than its box whenever its sibling
          overruns, and squeezing the panel would squeeze the part that
          carries the meaning.
        */}
        <Box sx={{ mb: 3 }}>
          <MonthlyQuotaPanel quota={quotaInfo} />
        </Box>

        {quotaInfo.bookings.length > 0 ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Buchungen in diesem Monat
            </Typography>
            <Divider />
            <Stack divider={<Divider />}>
              {quotaInfo.bookings.map((booking, index) => (
                <Box
                  key={`${booking.autopilot_id ?? 'none'}-${booking.kind}-${index}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 2,
                    py: 0.75,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap>
                      {/*
                        A booking outlives its autopilot, so a paused or
                        deleted one leaves the project unnamed rather than
                        dropping the amount still committed against it.
                      */}
                      {booking.project_name ?? 'Ohne Projekt'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {[
                        booking.schedule_type ? CADENCE_LABELS[booking.schedule_type] : null,
                        BOOKING_KIND_LABELS[booking.kind],
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                  >
                    {booking.amount > 0 ? `+${booking.amount}` : booking.amount} Prompts
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            In diesem Monat wurde noch nichts gebucht.
          </Alert>
        )}

        <Button variant="outlined" onClick={() => setAdjustDialogOpen(true)}>
          Kontingent korrigieren
        </Button>
      </CardContent>

      <AdjustQuotaDialog
        open={adjustDialogOpen}
        onClose={() => setAdjustDialogOpen(false)}
        tenantId={tenantId}
        bookings={quotaInfo.bookings}
        onAdjusted={onQuotaChanged}
      />
    </Card>
  );
};

export default AutopilotQuotaCard;
